// SupplierList.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types"; // Import PropTypes
import MDBox from "components/MDBox";
import api from "../../services/api";
import { useSnackbar } from "context/SnackbarContext";
import { useCompany } from "context/CompanyContext";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MDButton from "components/MDButton";
import LinearProgress from "@mui/material/LinearProgress";
import { useLanguage } from "context/LanguageContext";
import SupplierEdit from "./SupplierEdit";
import supplierTableData from "./supplierTableData";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";

const SupplierList = ({
  refreshTrigger,
  onRefreshComplete,
  focusSupplierRegistration,
  onFocusHandled,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [xmlTypeFilter, setXmlTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fetchError, setFetchError] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const { selectedCompany } = useCompany();
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const theme = useTheme();
  const refreshCompleteRef = useRef(onRefreshComplete);
  const fetchSequence = useRef(0);
  const focusHandledRef = useRef(null);

  useEffect(() => {
    refreshCompleteRef.current = onRefreshComplete;
  }, [onRefreshComplete]);

  // Remove these conflicting state variables
  // const [currentPageIndex, setCurrentPageIndex] = useState(0);
  // const [currentPageSize, setCurrentPageSize] = useState(20);

  const fetchSuppliers = useCallback(async () => {
    const sequence = ++fetchSequence.current;
    let succeeded = false;
    setLoading(true);
    setFetchError("");
    try {
      if (!selectedCompany) return;

      // Build URL with filters
      const params = new URLSearchParams({
        company_id: selectedCompany.id,
        imported: "True",
      });

      if (xmlTypeFilter) {
        params.append("xml_type", xmlTypeFilter);
      }

      if (searchFilter) {
        params.append("search", searchFilter);
      }

      const response = await api.get("/api/suppliers/", { params });
      const result = response.data;

      if (sequence !== fetchSequence.current) return;

      if (result.success) {
        const suppliersWithPrimary = result.suppliers.map((supplier) => {
          return {
            ...supplier,
            id: supplier.supplier_id, // Naudojame supplier_id vietoje id
            primary_warehouse: supplier.primary_warehouse || "",
            primary_group: supplier.primary_group || "",
            pvm_code: supplier.pvm_code || "",
            xml_type: supplier.xml_type || "",
          };
        });
        setRows(suppliersWithPrimary);
        if (
          focusSupplierRegistration &&
          searchFilter === focusSupplierRegistration &&
          focusHandledRef.current !== focusSupplierRegistration
        ) {
          const focused = suppliersWithPrimary.find(
            (item) => item.supplier_registration === focusSupplierRegistration
          );
          if (focused) {
            setSelectedSupplier(focused);
            setEditModalOpen(true);
          } else {
            showSnackbar(t("supplierNotImported"), "warning");
          }
          focusHandledRef.current = focusSupplierRegistration;
          onFocusHandled?.();
        }
        succeeded = true;
      } else {
        console.error("Error fetching suppliers:", result.error);
        setFetchError(result.error || t("errorOccurred"));
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      if (sequence === fetchSequence.current) setFetchError(t("errorOccurred"));
    } finally {
      if (sequence === fetchSequence.current) {
        setLoading(false);
        refreshCompleteRef.current?.(succeeded);
      }
    }
  }, [
    selectedCompany,
    xmlTypeFilter,
    searchFilter,
    focusSupplierRegistration,
    onFocusHandled,
    showSnackbar,
    t,
  ]);

  useEffect(() => {
    setSelectedSupplier(null);
    setEditModalOpen(false);
    setRows([]);
  }, [selectedCompany?.id]);

  useEffect(() => {
    if (selectedCompany) fetchSuppliers();
    else setLoading(false);
  }, [selectedCompany, refreshTrigger, fetchSuppliers]);

  useEffect(() => {
    if (!focusSupplierRegistration) return;
    focusHandledRef.current = null;
    setXmlTypeFilter("");
    setStatusFilter("all");
    setSearchInput(focusSupplierRegistration);
    setSearchFilter(focusSupplierRegistration);
  }, [focusSupplierRegistration]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchFilter(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleEdit = useCallback(
    (supplier) => {
      setSelectedSupplier(supplier);
      if (!editModalOpen) {
        setEditModalOpen(true);
      }
    },
    [editModalOpen]
  );

  const handleDelete = useCallback(
    async (supplierId) => {
      try {
        const response = await api.post("/api/delete/", {
          supplier_ids: [supplierId],
          company_id: selectedCompany.id,
        });
        const result = response.data;
        if (result.success) {
          showSnackbar("Tiekėjas sėkmingai ištrintas", "success");
          setSupplierToDelete(null);
          fetchSuppliers(); // Refresh the list
        } else {
          showSnackbar(`Klaida: ${result.error}`, "error");
        }
      } catch (error) {
        console.error("Error deleting supplier:", error);
        showSnackbar("Klaida trinant tiekėją", "error");
      }
    },
    [selectedCompany, showSnackbar, fetchSuppliers]
  );

  const handleUpdate = () => {
    fetchSuppliers();
  };

  const columns = useMemo(
    () => supplierTableData(handleEdit, setSupplierToDelete).columns,
    [handleEdit]
  );

  const data = useMemo(
    () =>
      rows.filter(
        (supplier) =>
          statusFilter === "all" ||
          (statusFilter === "missing" && !supplier.has_info) ||
          (statusFilter === "configured" && supplier.has_info)
      ),
    [rows, statusFilter]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex: tablePageIndex, pageSize: tablePageSize },
    gotoPage,
    setPageSize,
    pageOptions,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 20 },
      manualPagination: false,
    },
    useSortBy,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    gotoPage(0);
  }, [searchFilter, xmlTypeFilter, statusFilter, selectedCompany?.id, gotoPage]);

  const handleSuppliersPerPageChange = (event) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    gotoPage(0); // Reset to first page when changing page size
  };

  const handlePageChange = (event, value) => {
    gotoPage(value - 1); // Use React Table's gotoPage instead of local state
  };

  if (!selectedCompany) {
    return <Alert severity="info">{t("selectCompanyFirst")}</Alert>;
  }

  return (
    <MDBox p={{ xs: 2, md: 3 }}>
      <MDBox>
        {/* Filter Controls */}
        <MDBox
          display="flex"
          gap={2}
          mb={3}
          flexWrap="wrap"
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <TextField
            label={t("searchSuppliers")}
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 200 }, width: { xs: "100%", sm: "auto" } }}
            placeholder={t("searchByNameOrRegistration")}
          />
          <FormControl
            variant="outlined"
            size="small"
            sx={{ minWidth: { xs: "100%", sm: 150 }, width: { xs: "100%", sm: "auto" } }}
          >
            <InputLabel>{t("xmlType")}</InputLabel>
            <Select
              value={xmlTypeFilter}
              onChange={(e) => setXmlTypeFilter(e.target.value)}
              label={t("xmlType")}
            >
              <MenuItem value="">{t("all")}</MenuItem>
              <MenuItem value="quantitative">{t("quantitative")}</MenuItem>
              <MenuItem value="summary">{t("summary")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: "100%", sm: 190 } }}>
            <InputLabel>{t("supplierStatus")}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              label={t("supplierStatus")}
            >
              <MenuItem value="all">{t("all")}</MenuItem>
              <MenuItem value="missing">{t("missingSettings")}</MenuItem>
              <MenuItem value="configured">{t("configured")}</MenuItem>
            </Select>
          </FormControl>
        </MDBox>
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {fetchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fetchError}
          </Alert>
        )}
        {/* eslint-disable react/jsx-props-no-spreading */}
        <MDBox sx={{ overflowX: "auto" }}>
          <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              {headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id || index}>
                  {headerGroup.headers.map((column, colIndex) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={column.id || colIndex}
                      style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        background: theme.palette.action.hover,
                        padding: "10px 12px",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: theme.palette.text.secondary,
                        textAlign: "left",
                        width: `${column.width || "20%"}`,
                      }}
                    >
                      {column.render("Header")}
                      <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ padding: "24px 12px", textAlign: "center" }}
                  >
                    {t("noResults") || "Nėra tiekėjų pagal pasirinktus kriterijus."}
                  </td>
                </tr>
              ) : (
                page.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={row.id || rowIndex}>
                      {row.cells.map((cell, cellIndex) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id || cellIndex}
                          style={{
                            padding: "10px 12px",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            textAlign: "left",
                            width: `${cell.column.width || "20%"}`,
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </MDBox>
        {/* eslint-enable react/jsx-props-no-spreading */}

        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          mt={3}
        >
          <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FormControl variant="outlined" style={{ minWidth: 120 }}>
              <InputLabel id="suppliers-per-page-label">{t("perPage")}</InputLabel>
              <Select
                labelId="suppliers-per-page-label"
                value={tablePageSize} // Use React Table's pageSize
                onChange={handleSuppliersPerPageChange}
                label={t("perPage")}
              >
                {[20, 40, 100].map((pageSizeOption) => (
                  <MenuItem key={pageSizeOption} value={pageSizeOption}>
                    {t("showAll")} {pageSizeOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <span style={{ fontSize: "14px", color: "#666" }}>
              {t("showingSuppliersCount")
                .replace("{from}", data.length === 0 ? 0 : tablePageIndex * tablePageSize + 1)
                .replace("{to}", Math.min((tablePageIndex + 1) * tablePageSize, data.length))
                .replace("{total}", data.length)}
            </span>
          </MDBox>
          <Pagination
            count={Math.max(1, pageOptions.length)}
            page={tablePageIndex + 1} // Use React Table's pageIndex
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </MDBox>
        {selectedSupplier && (
          <SupplierEdit
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            supplier={selectedSupplier}
            onUpdate={handleUpdate}
          />
        )}
        <Dialog
          open={Boolean(supplierToDelete)}
          onClose={() => setSupplierToDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("delete")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("confirmDeleteSupplier") || "Ar tikrai norite ištrinti šį tiekėją?"}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => setSupplierToDelete(null)}
            >
              {t("cancel")}
            </MDButton>
            <MDButton
              variant="contained"
              color="error"
              onClick={() => handleDelete(supplierToDelete)}
            >
              {t("delete")}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </MDBox>
  );
};

// Define prop types
SupplierList.propTypes = {
  refreshTrigger: PropTypes.number,
  onRefreshComplete: PropTypes.func,
  focusSupplierRegistration: PropTypes.string,
  onFocusHandled: PropTypes.func,
};

SupplierList.defaultProps = {
  onRefreshComplete: undefined,
};

export default SupplierList;
